<script setup lang="ts">
import {
  IonApp,
  IonRouterOutlet,
  IonMenu,
  IonPage,
  IonHeader,
  IonBackButton,
  IonIcon,
  IonContent,
  IonToolbar,
  IonMenuButton,
  IonTitle,
  IonButtons,
  useIonRouter,
} from "@ionic/vue";
import { personCircle, caretDownOutline } from "ionicons/icons";
import { useUserStore } from "@/stores/user";
import { useWindowWidth } from "@/composition/window";
import { computed, reactive } from "vue";
import axios from "axios";
import Hamburger from "@/views/_partials/Hamburger.vue";
import { useContextStore } from "@/stores/context";
import { useRoute } from "vue-router";

const userStore = useUserStore();
const router = useIonRouter();
const route = useRoute();
const contextStore = useContextStore();

const { isMobile } = useWindowWidth();
const userIsOnboarding = computed(() => route.fullPath.includes("/onboarding"));

const userIsHomePageEvent = computed(() => route.fullPath.includes("/home"));
const userIsInEventPages = computed(() => route.fullPath.includes("/event") || route.fullPath.includes("/my-account"));
const userIsInManagementPages = computed(() => route.fullPath.includes("/my-account") || route.fullPath.includes("/manage-events"));
const userIsHomePage = computed(() => route.fullPath === "/home");

const state = reactive({
  profileDropdown: false,
});

const navigate = (endpoint: string) => {
  window.location.href = endpoint;
};

const toggleProfileDropdown = () => {
  state.profileDropdown = !state.profileDropdown;
};

userStore.localLogin();

if (userStore.userIsLoggedIn) {
  userStore
    .fetchMe()
    .then(() => {
      userStore.fetchRoles();
      if (route.fullPath.includes("onboarding/introduction")) {
        router.push("/home");
      }
    })
    .catch(() => {
      userStore.setUserLogout();
      router.push("/login");
    });
}

const logout = () => {
  state.profileDropdown = false;
  userStore.setUserLogout();
  router.push("/login");
};

axios.interceptors.response.use(undefined, (err) => {
  const serverPath = process.env.VUE_APP_SERVER_PATH;

  if (err.request.responseURL.includes(serverPath)) {
    if (err.request.status === 401) {
      // if you ever get an unauthorized, logout the user
      // userStore.setUserLogout();
      // userLoaded.value = true;
      // router.push("/login");
    }
    if (err.request.status === 403) {
      // User should not see permissions error
    } else if (err.request.status === 429) {
      // User throttled
    } else if ([500].includes(err.request.status)) {
      // Backend boom
    }
  }
  throw err;
});
</script>
<template>
  <ion-app>
    <ion-menu content-id="root-main-content">
      <Hamburger />
    </ion-menu>
    <ion-page id="root-main-content">
      <ion-header v-if="userStore.userIsLoggedIn && !userIsOnboarding">
        <ion-toolbar v-if="isMobile">
          <ion-buttons slot="start">
            <ion-back-button
              v-if="(userIsInManagementPages || userIsInEventPages) && !userIsHomePageEvent"
              color="white"
              :default-href="`/event/${contextStore.event.id}/home`"
            ></ion-back-button>
            <ion-back-button
              v-if="userIsHomePageEvent"
              color="white"
              :default-href="`/home`"
              :style="userIsHomePage ? { visibility: 'hidden' } : {}"
            ></ion-back-button>
          </ion-buttons>
          <ion-title>
            <img
              src="@/assets/Checkin_isotipo_White.svg"
              alt=""
              style="height: 3.5rem; margin: 0 auto; vertical-align: center"
              class="logo-white"
            />
          </ion-title>
          <ion-buttons slot="end">
            <ion-menu-button color="white"></ion-menu-button>
          </ion-buttons>
        </ion-toolbar>
        <ion-toolbar v-else>
          <div class="desktop-navbar">
            <ion-title>
              <div style="display: flex; align-items: center">
                <img
                  @click="navigate('/home')"
                  src="@/assets/Checkin_isotipo_White.svg"
                  alt=""
                  style="height: 3.5rem"
                  class="logo-white clickable"
                />
                <ul class="desktop-navbar__content">
                  <li @click="navigate('/home')">Home</li>
                  <li @click="navigate('/onboarding/new-event')">
                    Create event
                  </li>
                  <li @click="navigate('/manage-events')">Manage events</li>
                  <li
                    @click="
                      navigate(
                        'https://beamian-kb.atlassian.net/servicedesk/customer/portal/9'
                      )
                    "
                  >
                    Help center
                  </li>
                  <li
                    @click="navigate('https://beamian.com/lp-about-beamian/')"
                  >
                    About us
                  </li>
                  <li
                    @click="
                      navigate(
                        'https://beamian.com/lp-need-more-than-check-in/'
                      )
                    "
                  >
                    Need more?
                  </li>
                </ul>
              </div>
            </ion-title>
            <ion-buttons
              class="desktop-navbar__profile"
              slot="end"
              @click="toggleProfileDropdown()"
            >
              <ion-icon :icon="personCircle" size="large"></ion-icon>
              <div style="margin: 0 8px">{{ userStore.me.name }}</div>
              <ion-icon :icon="caretDownOutline"></ion-icon>
            </ion-buttons>
          </div>
        </ion-toolbar>
      </ion-header>
      <ion-content>
        <Suspense>
          <!-- component with nested async dependencies -->
          <ion-router-outlet :animated="isMobile" />

          <!-- loading state via #fallback slot -->
          <template #fallback> Loading... </template>
        </Suspense>
      </ion-content>
      <div
        v-if="!isMobile && state.profileDropdown"
        class="desktop-navbar__dropdown"
      >
        <div style="padding: 10px">
          {{ userStore.me.name }}
        </div>
        <div
          class="full-width"
          style="height: 1px; background-color: rgba(0, 0, 0, 0.232)"
        ></div>
        <div class="desktop-navbar__dropdown-menu">
          <div @click="navigate('/my-account')">My account</div>
          <div style="color: rgb(185, 5, 5)" @click="logout()">Logout</div>
        </div>
      </div>
    </ion-page>
  </ion-app>
</template>
<style lang="scss">
.logo-white {
  display: block;
}

h1 {
  font-size: 32px;
}

body.scanner-active ion-content {
  visibility: hidden;
  --background: transparent !important;
  --ion-background-color: transparent !important;
}

hr {
  margin: 1.5rem 0;
}
ion-grid {
  padding: 0;
}
ion-row {
  margin: -0.25rem;
}

ion-button {
  --border-radius: 4px;
}

ion-searchbar {
  padding-left: 0 !important;
}

p {
  margin-top: 0;
}

ion-card {
  margin: 0;
}

ion-footer {
  background: var(--ion-background-color, #fff);
}

ion-chip {
  border-radius: 4px;
  margin-inline-start: 0;
  ion-avatar {
    --border-radius: 0;
  }
}

ion-header {
  ion-toolbar {
    --background: var(--ion-color-primary);
    --color: #fff;
    ion-menu-button,
    ion-back-button {
      --color: #fff;
    }
    --padding-top: 0.5rem;
    --padding-bottom: 0.5rem;
    --padding-start: 0.5rem;
    --padding-end: 0.5rem;
  }
}

ion-toolbar.is-empty {
  --min-height: 0;
  --padding-top: 0;
  --padding-bottom: 0;
}

ion-card {
  &:not(:last-child) {
    margin-bottom: 1.5rem;
  }
}

ion-card-title {
  font-size: 1.5rem;
}

.ion-padding {
  --ion-padding: 1.5rem;
}

section {
  padding: 1rem 0;
}

html.ios {
  ion-footer {
    padding-bottom: 1rem;
    section {
      padding-bottom: 0;
    }
  }
  ion-fab {
    margin-bottom: 1rem;
  }
  section {
    padding: 1rem 0;
  }
}

.container {
  padding: 0 1rem;
  ion-item {
    margin: 0 -1rem;
    &.item-searchbar {
      margin: 0 -1rem;
      --ion-border-color: var(--ion-color-dark-contrast);
    }
  }
}

.box {
  padding: 1rem 1.5rem;
  border: 1px solid var(--ion-color-medium);
  border-radius: var(--radius);
  &.is-success {
    background: var(--ion-color-success);
  }
}

.full-height {
  height: 100%;
}

.full-width {
  width: 100%;
}

.text-center {
  text-align: center;
}

.text-white {
  color: #fff;
}

.colored-bg {
  background-color: #ffffff;
}

.desktop-navbar {
  display: flex;
  justify-content: space-between;
}

.clickable:hover {
  cursor: pointer;
}

.desktop-navbar__content {
  list-style-type: none;
  margin: 0 0 0 30px;
  padding: 0;
  display: flex;
}

.desktop-navbar__content li {
  margin: 0 15px;
  cursor: pointer;
  font-size: 16px;
}

.desktop-navbar__profile {
  margin-right: 30px;
  padding: 0 7px;
  border-radius: 20px;
  cursor: pointer;
}

.desktop-navbar__profile:hover {
  margin-right: 30px;
  background-color: rgba(255, 255, 255, 0.2);
}

.desktop-navbar__dropdown {
  position: absolute;
  top: 65px;
  right: 35px;
  z-index: 1000;
  width: 200px;
  background-color: white;
  border-radius: 20px;
  border: solid 1px rgb(227, 227, 227);
  -webkit-box-shadow: 0px 5px 10px 1px rgba(0, 0, 0, 0.1);
  -moz-box-shadow: 0px 5px 10px 1px rgba(0, 0, 0, 0.1);
  box-shadow: 0px 5px 10px 1px rgba(0, 0, 0, 0.1);
}

.desktop-navbar__dropdown-menu div {
  padding: 10px;
}

.desktop-navbar__dropdown-menu div:hover {
  cursor: pointer;
  background-color: rgb(245, 245, 245);
}

@media (min-width: 768px) {
  .colored-bg {
    background-color: #f5f7fa;
  }

  .container {
    width: 720px;
    margin: 2rem auto;
    padding: 1rem 2rem;
    background-color: #ffffff;
    color: #000000;
    border-radius: 20px;
  }

  ion-card {
    margin-bottom: 15px !important;
    margin-top: 15px;
  }

  .flex-desktop {
    display: flex;
  }
}

@media (min-width: 992px) {
  .container {
    width: 960px;
  }
}

@media (min-width: 1400px) {
  .container {
    width: 1140px;
  }
}
</style>
